import {
  Card,
  TextField,
  FormControl,
  Button,
  useMediaQuery,
  useTheme,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Dialog,
  Stack,
  Box,
  DialogContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import Translate from "../utils/Translate";
import Preloader from "../components/Preloader";
import Tooltip from "@mui/material/Tooltip";
import {
  getActorSkillsForNewPermit,
  getCompaniesForResponsibleView,
  getHolderActorForResponsibleView,
  getParentActorForNewActor,
  getPermitPdfBlobForResponsibleView,
  getPermitsForResponsibleView,
  getSkillGroupsForNewPermit,
  getSuggestedValuesForNewPermit,
  incativatePermitForResponsibleView,
  logoutForResponsibleView,
  markPermitAsReviewedForResponsibleView,
  sendNextSignerEmailForResponsibleView,
  updateHolderActorElectronicContactsForResponsibleView,
} from "../components/confirmPermits/permitApi";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import { CustomNoRowsOverlay } from "../utils/StyledGridOverlay";
import { dataGridLang } from "../components/MemToolBar";
import {
  AccountBox,
  Block,
  CheckCircle,
  Construction,
  HowToReg,
  Download,
  Edit,
  EventBusy,
  Logout,
  Mail,
  PendingActions,
  Person,
  Preview,
  Search,
  Recycling,
  Visibility
} from "@mui/icons-material";
import { IconAndLabelColumnHeader, TableAvatar2 } from "../utils/dataGrid";
import { DateRangeIcon } from "@mui/x-date-pickers";
import {
  formatPermitStatus,
  permitStatusToSortableValue,
  renderPermitStatus,
} from "../components/actor-permits/utils/permitStatus";
import DotMenu from "../components/DotMenu";
import { formatDateRange, formatDateWithMonthOffSet } from "../utils/dateTimeFormat";
import PermitCard from "../components/actor-permits/PermitCard";
import PermitDetailsDialog from "../components/actor-permits/responsible-view/PermitDetailsDialog";
import download from "downloadjs";
import YesOrNoDialog from "../components/YesOrNoDialog";
import { useSnackbar } from "notistack";
import PersonElectronicContactsCrud from "../components/actors/crud/PersonElectronicContactsCrud";
import InactivateActorPermitDialog from "../components/actor-permits/InactivateActorPermitDialog";
import { useStaticRangePicker } from "@mui/x-date-pickers-pro/internals/hooks/useStaticRangePicker";
import ActorPermitFlowDialogContainer from "../components/actor-permits/ActorPermitFlowDialogContainer";

const defaultColumnVisibility = {
  responsibleActorId: false,
  responsibleName: false,
  responsibleTitle: false,
  permitNotSigned: false,
  fActorPermit: false,
  actorId: false,
  validTo: false,
  reviewIsDue: false,
  reviewDate: false,
  reviewer: false,
};

async function getPermits(dataId) {
  const response = await getPermitsForResponsibleView(dataId);

  if (response?.isSuccessful) {
    // Note that some keys are remapped here
    return response.value.map((a) => ({
      ...a,
      parentActorName: a.parentActorName !== "ePortMaster" ? a.parentActorName : "",
      responsibleName:
        a.responsibleActorId !== 4 // 4 ePortMaster ActorId
          ? a.responsibleActorName
          : "",
      machineGroup: a.skillGroupDescription,
      machineTypes: a.skills?.map((s) => s.shortCode).join(", "),
      isSignedByResponsible: !!a.responsibleSignatureId,
      isSignedByStudent: !!a.signatureId,
    }));
  }

  return [];
}

async function getCompanies() {
  const response = await getCompaniesForResponsibleView();

  if (response.isSuccessful) {
    return response.companies;
  }
}

const getDotMenuActions = ({
  actorPermit,
  onSendPermitSignReminder,
  onDownloadActorPermit,
  onInactivateActorPermit,
  onMarkPermitAsReviewed,
  onEditActorElectronicContacts,
  onNewPermitFromExisting
}) => {
  const manualPermit = actorPermit.blobFile && true;
  const isSignedByBoth = actorPermit.isSignedByResponsible && actorPermit.isSignedByStudent;

  // körtillståndet är giltigt ifall validTo är null eller har passerat
  const isActive = !actorPermit.validTo || new Date(actorPermit.validTo) > new Date();

  // för att kunna tas bort ska körtillståndet vara inaktivt och inte signerat av båda, manuella körtilstånd signeras inte
  const deletePossible = !isActive || (!manualPermit && !isSignedByBoth);
  const inactivatePossible = !deletePossible && isActive;

  const editContactDetailsAllowed = isActive 
    && !manualPermit
    && actorPermit.isSignedByResponsible
    && !actorPermit.isSignedByStudent;

  return [
    {
      icon: <Edit />,
      disabled: !editContactDetailsAllowed,
      text: Translate.get("EditContactDetails"),
      onClick: () => onEditActorElectronicContacts(actorPermit)
    },
    {
      icon: <Mail />,
      disabled: isSignedByBoth,
      text: Translate.get("ResendSignarureRequest"),
      onClick: () => onSendPermitSignReminder(actorPermit),
      hide: actorPermit.blobFile !== null,
    },
    {
      icon: <Download />,
      text: Translate.get("Download"),
      onClick: () => onDownloadActorPermit(actorPermit),
    },
    {
      icon: <Block />,
      disabled: !inactivatePossible,
      text: Translate.get("InactivatePermit"),
      onClick: () => onInactivateActorPermit(actorPermit),
    },
    {
      icon: <Preview />,
      disabled: !isSignedByBoth,
      text: Translate.get("MarkAsReviewed"),
      onClick: () => onMarkPermitAsReviewed(actorPermit),
    },
    {
      icon: <Recycling />,
      text: Translate.get("CreateFromThis"),
      onClick: () => onNewPermitFromExisting(actorPermit)
    },
  ];
};

export default function PermitsResponsibleViewPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [permits, setPermits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyDataId, setSelectedCompanyDataId] = useState(null);
  const [showPermitDetails, setShowPermitDetails] = useState(null);
  const [actorPermitToInactivate, setActorPermitToInactivate] = useState(null);
  const [editPermitContactsForActorId, setEditPermitContactsForActorId] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(defaultColumnVisibility);
  const [createFromExistingPermit, setCreateFromExistingPermit] = useState(null);
  const [createFromExistingPermitWithCloning, setCreateFromExistingPermitWithCloning] = useState(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const apiRef = useGridApiRef();
  const localizedTextsMap = dataGridLang();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    async function init() {
      setIsLoading(true);
      const companies = await getCompanies();
      const firstDataId = companies && companies.length > 0 ? companies[0].dataId : null;
      setCompanies(companies ?? []);
      setSelectedCompanyDataId(firstDataId);
      const loadedPermits = await getPermits(firstDataId);
      setPermits(loadedPermits);
      setIsLoading(false);
    }
    init();
  }, []);

  async function showSuccessAndReload(messageTranslationKey) {
    showSuccess(messageTranslationKey);
    setIsLoading(true);
    const loadedPermits = await getPermits(selectedCompanyDataId);
    setPermits(loadedPermits);
    setIsLoading(false);
  }

  function showSuccess(messageTranslationKey) {
    const successMessage = enqueueSnackbar(Translate.get(messageTranslationKey ?? "Ready"), {
      variant: "success",
      autoHideDuration: 6000,
      onClick: () => closeSnackbar(successMessage),
    });
  }

  function showError(errorMessageTranslationKey) {
    const errorMessage = enqueueSnackbar(Translate.get(errorMessageTranslationKey ?? "SomethingFailed"), {
      variant: "error",
      autoHideDuration: 6000,
      onClick: () => closeSnackbar(errorMessage),
    });
  }

  async function handleCompanySelected(companyDataId) {
    setIsLoading(true);
    setSelectedCompanyDataId(companyDataId);
    const loadedPermits = await getPermits(companyDataId);
    setPermits(loadedPermits);
    setIsLoading(false);
  }

  async function handleSendNextPermitSignEmail(permit) {
    const response = await sendNextSignerEmailForResponsibleView(
      permit.actorPermitId,
      permit.actorPermitGroupId,
      permit.dataId
    );
    if (response.isSuccessful) {
      showSuccess("SignReminderSent");
    } else {
      showError(response.errorMessageTranslationKey);
    }
  }

  async function handleDownloadActorPermit(permit) {
    setIsLoading(true);

    const blob = await getPermitPdfBlobForResponsibleView(permit.actorPermitId, permit.dataId);
    if (blob.isSuccessful === false) {
      showError(blob.errorMessageTranslationKey);
      return;
    }

    const filename = `${Translate.get("ActorPermit")}_${permit.skillGroupDescription}_${permit.actorName}.pdf`;
    download(blob, filename, blob.type);
    setIsLoading(false);
  }

  async function handleInactivateActorPermit(inactivationData) {
    const response = await incativatePermitForResponsibleView(inactivationData);
    if (response.isSuccessful) {
      showSuccessAndReload();
      setActorPermitToInactivate(null);
    } else {
      showError(response.errorMessageTranslationKey);
    }
  }

  async function handleMarkPermitAsReviewed(actorPermit) {
    const response = await markPermitAsReviewedForResponsibleView(actorPermit.actorPermitGroupId, actorPermit.actorPermitId, actorPermit.dataId);
    if (response.isSuccessful) {
      showSuccessAndReload();
    } else {
      showError(response.errorMessageTranslationKey);
    }
  }

  async function handleLogout() {
    await logoutForResponsibleView();
    navigate("/login/permit-responsible-view");
  }

  let columns = [
    {
      field: "actorName",
      headerName: Translate.get("ActorName"),
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={Person} label={Translate.get("ActorName")} />,
      display: "flex",
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <TableAvatar2
            image={params.row.actorPictureUrl}
            name={params.row.actorName}
            email={params.row.actorEmail}
            orgNo={params.row.actorOrgNo}
            externalActorNo={params.row.externalActorNo}
          />
        </div>
      ),
    },
    {
      field: "machineGroup",
      headerName: Translate.get("MachineGroup"),
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={Construction} label={Translate.get("MachineGroup")} />,
    },
    {
      field: "validTime",
      headerName: Translate.get("ValidTime"),
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={DateRangeIcon} label={Translate.get("ValidTime")} />,
      valueGetter: (value, row, column, apiRef) => formatDateRange(row.validFrom, row.validTo),
    },
    {
      field: "responsibleName",
      headerName: Translate.get("Responsible"),
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={HowToReg} label={Translate.get("Responsible")} />,
      display: "flex",
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <TableAvatar2
            showNameAndEmailOnly={true}
            name={params.row.responsibleActorName}
            email={params.row.responsibleActorEmail}
          />
        </div>
      ),
    },
    {
      field: "responsibleTitle",
      headerName: Translate.get("ResponsibleTitle"),
      disableColumnMenu: true,
      flex: 1,
      hide: true,
      renderHeader: () => <IconAndLabelColumnHeader icon={HowToReg} label={Translate.get("ResponsibleTitle")} />,
    },
    {
      field: "customPermitStatus",
      headerName: Translate.get("Status"),
      flex: 0.5,
      minWidth: 100,
      filterable: false,
      valueFormatter: (value, row, column, apiRef) => formatPermitStatus(value),
      renderHeader: () => <IconAndLabelColumnHeader icon={CheckCircle} label={Translate.get("Status")} />,
      display: "flex",
      renderCell: (params) => renderPermitStatus(params.value, params.row),
      // This valueGetter will allow us to sort the status column, without using sortComparator
      valueGetter: (value, row, column, apiRef) => permitStatusToSortableValue(row),
    },
    {
      field: "reviewIsDue",
      headerName: Translate.get("ReviewNeeded"),
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={Preview} label={Translate.get("ReviewNeeded")} />,
      type: "boolean",
      renderCell: (params) =>
        params.row.reviewIsDue ? (
          <div>
            <Tooltip title={Translate.get("ReviewNeeded")}>
              <PendingActions />
            </Tooltip>
          </div>
        ) : (
          <div></div>
        ),
    },
    {
      field: "reviewDate",
      headerName: Translate.get("ReviewDate"),
      flex: 1,
      type: "date",
      renderHeader: () => <IconAndLabelColumnHeader icon={Preview} label={Translate.get("ReviewDate")} />,
      valueGetter: (value, row, column, apiRef) => (row.reviewDate ? new Date(row.reviewDate) : ""),
    },
    {
      field: "reviewer",
      headerName: Translate.get("Reviewer"),
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={Preview} label={Translate.get("Reviewer")} />,
      valueGetter: (value, row, column, apiRef) => row.reviewer,
    },
    {
      field: "customViewDetails",
      headerName: Translate.get("Permit"),
      flex: 0,
      sortable: false,
      filterable: false,
      disableExport: true,
      renderHeader: () => <div></div>,
      renderCell: (params) => (
        <Button
          className="regularButtonWithIcon stdLightLoader"
          variant="outlined"
          onClick={(event) => {
            event.stopPropagation();
            setShowPermitDetails(params.row);
          }}
        >
          <Visibility />
        </Button>
      ),
    },
    {
      field: "dotview",
      headerName: "Menu",
      flex: 0,
      maxWidth: 50,
      align: "right",
      sortable: false,
      filterable: false,
      disableExport: true,
      renderHeader: () => <div></div>,
      display: "flex",
      renderCell: (params) => (
        <DotMenu
          content={getDotMenuActions({
            actorPermit: params.row,
            onSendPermitSignReminder: handleSendNextPermitSignEmail,
            onDownloadActorPermit: handleDownloadActorPermit,
            onInactivateActorPermit: (actorPermit) => setActorPermitToInactivate(actorPermit),
            onMarkPermitAsReviewed: handleMarkPermitAsReviewed,
            onEditActorElectronicContacts: (permit) => setEditPermitContactsForActorId(permit.actorId),
            onNewPermitFromExisting: (permit) => { 
              setCreateFromExistingPermit(permit);
              setCreateFromExistingPermitWithCloning(permit);
            }
          })}
          horizontal
        />
      ),
    },
    {
      field: "validTo",
      headerName: "Tillstånd giltigt t.o.m",
      disableExport: true,
    },
    {
      field: "responsibleActorId",
      headerName: "Systemstatus: Id Ansvarig",
      sortable: false,
      disableExport: true,
    },
    {
      field: "actorId",
      headerName: "Systemstatus: Id Person",
      sortable: false,
      disableExport: true,
    },
    {
      field: "fActorPermit",
      headerName: "Systemstatus: Tillstånd snart utgående",
      sortable: false,
      disableExport: true,
      valueGetter: (value, row, column, apiRef) => {
        const fdate = formatDateWithMonthOffSet(new Date(), 3);
        let chk = 0;
        if (row.validTo) {
          if (row.validTo < formatDateRange(fdate) && row.validTo > formatDateRange(new Date())) {
            chk = 1;
          }
        }
        return chk;
      },
    },
    {
      field: "permitNotSigned",
      headerName: "Systemstatus: Ej signerat",
      sortable: false,
      disableExport: true,
      valueGetter: (value, row, column, apiRef) => {
        let chk = 0;
        const chkArr = [-1, 6];
        if ((!row.signatureId || !row.responsibleSignatureId) && !chkArr.includes(permitStatusToSortableValue(row))) {
          chk = 1;
        }
        return chk;
      },
    },
  ];

  const listViewColumns = {
    field: "listColumn",
    display: "flex",
    renderCell: (params) => (
      <div style={{ width: "100%" }}>
        <PermitCard
          showPermitDetailsButton
          key={params.row.actorPermitId}
          actorName={params.row.actorName}
          cardView={true}
          {...params.row}
          extraActions={getDotMenuActions({
            actorPermit: params.row,
            onSendPermitSignReminder: handleSendNextPermitSignEmail,
            onDownloadActorPermit: handleDownloadActorPermit,
            onInactivateActorPermit: (actorPermit) => setActorPermitToInactivate(actorPermit),
            onMarkPermitAsReviewed: handleMarkPermitAsReviewed,
            onEditActorElectronicContacts: (permit) => setEditPermitContactsForActorId(permit.actorId),
            onNewPermitFromExisting: (permit) => {
              setCreateFromExistingPermit(permit);
              setCreateFromExistingPermitWithCloning(permit);
            }
          })}
        />
      </div>
    ),
  };

  const hasSingleCompany = !companies || companies.length <= 1;

  return (
    <>
      {isLoading && <Preloader />}

      {showPermitDetails && (
        <Dialog
          open
          onClose={() => setShowPermitDetails(null)}
          className="bigCourseDialog"
          maxWidth={false}
          scroll="paper"
        >
          <PermitDetailsDialog
            actorPermitId={showPermitDetails.actorPermitId}
            dataId={showPermitDetails.dataId}
            onClose={() => setShowPermitDetails(null)}
          />
        </Dialog>
      )}

      {editPermitContactsForActorId
      && <Dialog open>
            <PersonElectronicContactsCrud
              actorId={editPermitContactsForActorId} 
              dataId={selectedCompanyDataId}
              apiFetchActor={(actorId) => getHolderActorForResponsibleView(actorId, selectedCompanyDataId)}
              apiSaveActorElectronicContacts={updateHolderActorElectronicContactsForResponsibleView}
              onActorSaved={() => showSuccessAndReload("ChangesSaved") }
              onClose={() => setEditPermitContactsForActorId(null)}
            />
        </Dialog>}

      <PageHeader
        companies={companies}
        selectedCompanyDataId={selectedCompanyDataId}
        hasSingleCompany={hasSingleCompany}
        isSmallScreen={isSmallScreen}
        handleCompanySelected={handleCompanySelected}
        handleLogout={handleLogout}
      />

      {isSmallScreen ? (
        <DataGridPremium
          rows={permits}
          className="stdVisible noDividers"
          columns={columns}
          getRowId={(row) => row.actorPermitId}
          localeText={localizedTextsMap}
          rowHeight={440}
          apiRef={apiRef}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHere")),
          }}
          disableRowSelectionOnClick
          disableDensitySelector
          disableColumnSelector
          disableColumnMenu
          listView
          listViewColumn={listViewColumns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
          hideFooter
        />
      ) : (
        <Card>
          <DataGridPremium
            rows={permits}
            className="stdVisible dgDynIconText"
            columns={columns}
            getRowId={(row) => row.actorPermitId}
            localeText={localizedTextsMap}
            rowHeight={95}
            apiRef={apiRef}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHere")),
            }}
            disableRowSelectionOnClick
            disableDensitySelector
            disableColumnSelector
            disableColumnMenu
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
            hideFooter
          />
        </Card>
      )}
      {actorPermitToInactivate 
      && <InactivateActorPermitDialog actorPermit={actorPermitToInactivate}
        onInactivateClick={handleInactivateActorPermit}
        onClose={() => setActorPermitToInactivate(null)}
      />}

      {createFromExistingPermit
      && (
          (createFromExistingPermitWithCloning === null 
            && <YesOrNoDialog open 
                title={Translate.get("CreateFromThis")}
                text={Translate.get("RepsonsibleViewClonePermitPrompt")}
                onYes={() => setCreateFromExistingPermitWithCloning(true)}
                onNo={() => setCreateFromExistingPermitWithCloning(false)}
                onCancel={() => {
                  setCreateFromExistingPermitWithCloning(null);
                  setCreateFromExistingPermit(null);
                }}  
              />) 
        ||
          <Dialog open>
            <ActorPermitFlowDialogContainer actorIds={[createFromExistingPermit?.actorId]}
                cloneFromActorPermit={createFromExistingPermitWithCloning && createFromExistingPermit}
                responsibleActorId={createFromExistingPermit?.responsibleActorId}
                showActorSearch={false}
                showInPermitResponsibleView={true}
                apiGetActor={(actorId) => getHolderActorForResponsibleView(actorId, selectedCompanyDataId)}
                dataId={selectedCompanyDataId}
                apiGetActorSkillForNewPermit={(actorId) => getActorSkillsForNewPermit(selectedCompanyDataId, actorId)}
                apiGetActorSkillGroupsForNewPermit={() => getSkillGroupsForNewPermit(selectedCompanyDataId)}
                apiGetParentActorForDataId={() => getParentActorForNewActor(selectedCompanyDataId)}
                apiGetSuggestedValuesForNewPermit={(skillGroupId) => getSuggestedValuesForNewPermit(selectedCompanyDataId, skillGroupId)}
              />
          </Dialog>)
      }
    </>
  );
}

function PageHeader({
  companies,
  selectedCompanyDataId,
  hasSingleCompany,
  isSmallScreen,
  handleCompanySelected,
  handleLogout,
}) {
  return (
    <Box marginBottom="16px">
      {hasSingleCompany && (
        <div className="stdFlexRowTop">
          <h2>{Translate.get("PermitResponsibleViewPageTitle")}</h2>
          <Button
            className="regularButtonWithIcon stdLightLoader"
            sx={{ textWrap: "nowrap" }}
            variant="text"
            startIcon={<Logout width={"30px"} />}
            onClick={handleLogout}
          >
            {Translate.get("LogOut")}
          </Button>
        </div>
      )}

      {!hasSingleCompany && isSmallScreen && (
        <>
          <div className="stdFlexRowTop">
            <h2>{Translate.get("PermitResponsibleViewPageTitle")}</h2>
            <Button
              className="regularButtonWithIcon stdLightLoader"
              sx={{ textWrap: "nowrap" }}
              variant="text"
              startIcon={<Logout width={"30px"} />}
              onClick={handleLogout}
            >
              {Translate.get("LogOut")}
            </Button>
          </div>
          <FormControl fullWidth sx={{ marginTop: "8px" }}>
            <InputLabel id="companies">{Translate.getActorType(1)}</InputLabel>
            <Select
              fullWidth
              labelId="companies"
              id="companies"
              value={selectedCompanyDataId ?? ""}
              label={Translate.getActorType(1)}
              onChange={(event) => handleCompanySelected(event.target.value)}
              renderValue={(value) => (value ? companies.find((c) => c.dataId == value).companyName : "")}
            >
              {companies.map((company) => (
                <MenuItem key={company.dataId} value={company.dataId}>
                  <CompanyMenuItem company={company} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {!hasSingleCompany && !isSmallScreen && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <h2>{Translate.get("PermitResponsibleViewPageTitle")}</h2>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={2}>
            <FormControl sx={{ width: "300px" }}>
              <InputLabel id="companies">{Translate.getActorType(1)}</InputLabel>
              <Select
                labelId="companies"
                id="companies"
                value={selectedCompanyDataId ?? ""}
                label={Translate.getActorType(1)}
                onChange={(event) => handleCompanySelected(event.target.value)}
                renderValue={(value) => (value ? companies.find((c) => c.dataId == value).companyName : "")}
              >
                {companies.map((company) => (
                  <MenuItem key={company.dataId} value={company.dataId}>
                    <CompanyMenuItem company={company} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              className="regularButtonWithIcon stdLightLoader"
              sx={{ textWrap: "nowrap" }}
              variant="text"
              startIcon={<Logout width={"30px"} />}
              onClick={handleLogout}
            >
              {Translate.get("LogOut")}
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}

function CompanyMenuItem({ company }) {
  const addressLine1 = company.addressStreet2;
  const addressLine2 =
    company.addressZipCode && company.addressCity
      ? `${company.addressZipCode} ${company.addressCity}`
      : company.addressZipCode
      ? company.addressZipCode
      : company.addressCity
      ? company.addressCity
      : "";
  return (
    <div>
      <div>{company.companyName}</div>
      {addressLine1 && (
        <Typography
          noWrap
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {addressLine1}
        </Typography>
      )}
      {addressLine2 && (
        <Typography
          noWrap
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {addressLine2}
        </Typography>
      )}
    </div>
  );
}
